import {
    type Dispatch,
    type MutableRefObject,
    type RefObject,
    type SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useAdapter } from '../../application/data/use-adapter';
import type { ResolvedColumn } from '../../domain';
import type { AdapterSnapshot, TableAdapter } from '../../ports';
import type { DropdownKind, OperatorOption } from './types';

export interface UseSearchArgs<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
}

export interface SearchBase<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    snapshot: AdapterSnapshot<T>;
    inputRef: RefObject<HTMLInputElement>;
    containerRef: RefObject<HTMLDivElement>;
    timer: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
    focusInput: () => void;
    pendingColumn: ResolvedColumn<T> | null;
    setPendingColumn: Dispatch<SetStateAction<ResolvedColumn<T> | null>>;
    pendingOperator: OperatorOption | null;
    setPendingOperator: Dispatch<SetStateAction<OperatorOption | null>>;
    inputValue: string;
    setInputValue: Dispatch<SetStateAction<string>>;
    dropdown: DropdownKind;
    setDropdown: Dispatch<SetStateAction<DropdownKind>>;
    onInputChange: (value: string) => void;
    onInputFocus: () => void;
    removeChip: (key: string) => void;
    removeLastChip: () => void;
}

export function useSearch<T>({
    adapter,
    columns,
}: UseSearchArgs<T>): SearchBase<T> {
    const snapshot = useAdapter(adapter);
    const [pendingColumn, setPendingColumn] =
        useState<ResolvedColumn<T> | null>(null);
    const [pendingOperator, setPendingOperator] =
        useState<OperatorOption | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [dropdown, setDropdown] = useState<DropdownKind>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const prevAdapter = useRef(adapter);

    useEffect(() => {
        if (prevAdapter.current === adapter) return;
        prevAdapter.current = adapter;
        clearTimeout(timer.current);
        setPendingColumn(null);
        setPendingOperator(null);
        setDropdown(null);
        setInputValue('');
    }, [adapter]);

    useEffect(
        () => () => {
            clearTimeout(timer.current);
        },
        [],
    );

    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    const onInputChange = useCallback(
        (value: string) => {
            setInputValue(value);
            if (pendingColumn) return;
            setDropdown(value.length > 0 ? 'column' : null);
        },
        [pendingColumn],
    );

    const onInputFocus = useCallback(() => {
        if (!pendingColumn) {
            if (inputValue.length > 0) setDropdown('column');
            return;
        }
        if (pendingColumn.filter === 'set') setDropdown('value');
        else if (pendingColumn.filter === 'date')
            setDropdown(pendingOperator ? 'date' : 'operator');
        else if (pendingColumn.filter === 'number' && !pendingOperator)
            setDropdown('operator');
    }, [pendingColumn, pendingOperator, inputValue]);

    const removeChip = useCallback(
        (key: string) => {
            adapter.setFilter(key, null);
        },
        [adapter],
    );

    const removeLastChip = useCallback(() => {
        const last = snapshot.filters.at(-1);
        if (last) adapter.setFilter(last.key, null);
    }, [snapshot.filters, adapter]);

    return {
        adapter,
        columns,
        snapshot,
        inputRef,
        containerRef,
        timer,
        focusInput,
        pendingColumn,
        setPendingColumn,
        pendingOperator,
        setPendingOperator,
        inputValue,
        setInputValue,
        dropdown,
        setDropdown,
        onInputChange,
        onInputFocus,
        removeChip,
        removeLastChip,
    };
}
